import React, { useState } from "react";
import { ELightPath } from "../../../../Model/CaptureProfile";
import { CartesianCoordinate } from "../../../../Model/Coordinate";
import { StageOffsetCalibration } from "../../../../Model/ImagerCalibration";
import { getOneImagerCapabilities } from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import LightPathUtil from "../../../../Utility/LightPathUtil";
import CommandPanel from "../../../CommandPanel/CommandPanel";
import TabPanel from "../../../PlateTypeEditor/TabPanel";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import ImagerUtil from "../../../../Utility/ImagerUtil";
import ServicingTable from "../../../Common/ServicingTable";
import ImagerManualControl from "../../../Imager/ImagerManualControl";

type Props = {
  imagerIdentifier: string;
  stageOffset: StageOffsetCalibration;
  setStageOffset: (stageOffset: StageOffsetCalibration) => void;
  backBtn: () => void;
};

export default function CameraOffsets({
  imagerIdentifier,
  stageOffset,
  setStageOffset,
  backBtn,
}: Props) {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const imagerCapabilities = useTypedSelector((state) =>
    getOneImagerCapabilities(state, imagerIdentifier)
  );
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const imagerState = moduleState.imager;
  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  if (stageOffset == null) {
    throw new Error("Imager stage offset is null");
  }

  const onInputPosition = (
    lightPath: ELightPath,
    position: CartesianCoordinate
  ) => {
    const objectiveValue = ImagerUtil.getObjectiveValue(
      imagerCapabilities,
      imagerState,
      lightPath
    );
    const filterCubeValue = ImagerUtil.getFilterCubeValue(
      imagerCapabilities,
      imagerState,
      lightPath
    );

    let newStageOffset: StageOffsetCalibration = {
      ...stageOffset,
    };

    if (lightPath === ELightPath.Visible) {
      newStageOffset.coordinatesOnPrimaryLightpath = [
        ...newStageOffset.coordinatesOnPrimaryLightpath,
      ];
      newStageOffset.coordinatesOnPrimaryLightpath[selectedIndex] = position;
    } else {
      newStageOffset.coordinatesOnSecondaryLightpath = [
        ...newStageOffset.coordinatesOnSecondaryLightpath,
      ];
      newStageOffset.coordinatesOnSecondaryLightpath[selectedIndex] = position;
    }

    if (objectiveValue.objectiveIndex >= 0) {
      newStageOffset.objectiveIndexUsedForCalibration =
        objectiveValue.objectiveIndex;
    }
    if (filterCubeValue.filterCubeIndex >= 0) {
      newStageOffset.filterCubeIndexUsedForCalibration =
        filterCubeValue.filterCubeIndex;
    }
    setStageOffset(newStageOffset);
  };

  const getData = (lightPath: ELightPath) => {
    let offset =
      lightPath === ELightPath.Visible
        ? stageOffset.coordinatesOnPrimaryLightpath
        : stageOffset.coordinatesOnSecondaryLightpath;
    return offset.map((row, idx) => ({
      name: "Position (" + (idx + 1) + ")",
      id: idx.toString(),
      selected: idx === selectedIndex,
      ...row,
    }));
  };

  const tabPanelContents: { [index: string]: React.ReactNode } = {};

  for (const lightPath of imagerCapabilities.lightpathCollection.allIds) {
    tabPanelContents[LightPathUtil.toName(lightPath)] = (
      <ServicingTable
        imagerIdentifier={imagerIdentifier}
        imagerState={imagerState}
        dropNumber={0}
        onDropNumberChanged={() => {}}
        maxDrop={0}
        data={getData(lightPath)}
        onRowSelected={(row) => setSelectedIndex(Number.parseInt(row.id))}
        onRowUpdated={(row) =>
          onInputPosition(lightPath, {
            x: row.x,
            y: row.y,
            z: row.z,
          })
        }
        style={{
          padding: "8px",
        }}
      />
    );
  }

  return (
    <CommandPanel
      style={{
        overflowY: "auto",
        WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
      }}
    >
      <CommandPanel.DarkBackArrowHeader
        title="Camera Offsets"
        onBackArrowClick={backBtn}
        style={{
          height: 60,
        }}
      />

      <TabPanel
        id="CameraOffset"
        collapsible={false}
        disabled={false}
        allowChange={true}
        defaultSelectedIndex={selectedTabIndex}
        onClickTab={(header) =>
          setSelectedTabIndex(
            Object.keys(tabPanelContents).findIndex((key) => key === header)
          )
        }
      >
        {tabPanelContents}
      </TabPanel>

      <ImagerManualControl
        wellLimitEnabled={false}
        imagerIdentifier={imagerIdentifier}
        style={{
          overflow: "none",
        }}
      />
    </CommandPanel>
  );
}
